import type { BossId } from '@bioforge/protocol';
import { gsap } from 'gsap';
import { Container, FillGradient, Graphics, Text, type Sprite } from 'pixi.js';
import { HAT_FRAME } from '../art/characters.js';
import { microbeIdle, idle, motionReduced } from '../ui/motion.js';
import { artSprite } from '../ui/textures.js';
import { COLOR, textStyle } from '../ui/theme.js';
import { vertical } from '../ui/widgets.js';

/**
 * Банка макета (компонент «Банка»): крышка, стекло, сетка, микробы, капсулы, босс.
 * Принимает готовую картинку клеток: главный экран — из строк макета, бой — из состояния движка
 * (`jar-model.ts`). Перерисовка пошаговая: микробы, отметки и босс живут между ходами,
 * поэтому их анимация не сбивается; капсулы и туман перерисовываются целиком.
 * Анимация очистки клеток обсуждается отдельно и пока не рисуется.
 */

export type CapsuleSide = 'L' | 'R' | 'U' | 'D' | 'S';

export type JarCell =
  | { readonly kind: 'empty' }
  | {
      readonly kind: 'microbe';
      readonly color: number;
      readonly armor?: 'normal' | 'gold';
      readonly mark?: boolean;
    }
  | {
      readonly kind: 'piece';
      readonly color: number;
      readonly side: CapsuleSide;
      readonly active?: boolean;
    }
  | { readonly kind: 'ghost'; readonly side: CapsuleSide }
  /** Здесь босс родит микроба (отмеченная клетка). */
  | { readonly kind: 'spawn' }
  /** Стенка сосуда: клетка вне формы поля. */
  | { readonly kind: 'wall' }
  /** Щупальце гибрида в сосуде. */
  | { readonly kind: 'part' };

export interface JarBoss {
  readonly id: BossId;
  /** Прямоугольник тела в клетках. */
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly scream?: boolean;
  /** Число в красном кружке — капсул до способности (или до пробуждения). */
  readonly timer?: string;
}

export interface JarModel {
  readonly width: number;
  readonly height: number;
  /** Клетки по строкам сверху вниз. */
  readonly cells: readonly JarCell[];
  readonly boss?: JarBoss | null;
  /** Колонки зоны появления капсулы (штриховка в верхнем ряду). */
  readonly spawnColumns?: readonly number[];
  /** Затуманенные колонки («Токсин»): содержимое скрыто. */
  readonly fog?: { readonly x: number; readonly width: number } | null;
  readonly frost?: boolean;
  /** Значки цвета на капсулах (различимость не только по цвету). */
  readonly symbols?: boolean;
  /** Надетые скины из магазина (SPEC.md, раздел 18): только внешний вид. */
  readonly skin?: JarSkin | null;
}

export interface JarSkin {
  /** Колпак или очки на микробах по индексу цвета. */
  readonly hats?: Readonly<Partial<Record<number, 'santa' | 'shades'>>>;
  readonly vessel?: 'aquarium' | 'snowglobe' | null;
  readonly capsule?: 'candy' | null;
}

/** Оформление сосуда: крышка, обод, стекло. Стекло тёмное — сетка и клетки остаются видны. */
interface VesselLook {
  readonly lid: number;
  readonly lidStripe: number;
  readonly rim: readonly [number, number];
  readonly glass: readonly [number, number, number];
}

const VESSEL_LOOKS: Readonly<Record<'default' | 'aquarium' | 'snowglobe', VesselLook>> = {
  default: {
    lid: 0xc27a3e,
    lidStripe: 0xb06a32,
    rim: [0xf2c27b, 0x7a3f1c],
    glass: [0x0b2433, 0x0e3446, 0x135a55],
  },
  aquarium: {
    lid: 0x9fb8c8,
    lidStripe: 0x7f9fb3,
    rim: [0xe6f3fa, 0x5e7e92],
    glass: [0x0b3550, 0x0f5c7a, 0x1a8c8c],
  },
  snowglobe: {
    lid: 0xc23a4a,
    lidStripe: 0xffd23a,
    rim: [0xffe08a, 0x8a6a2e],
    glass: [0x14284a, 0x22466e, 0x3f6f96],
  },
};

const PAL = [0x9b6bff, 0xff4f7b, 0xffd23a] as const;
const LIGHT = [0xd2bdff, 0xffa9be, 0xfff3b0] as const;
const DARK = [0x5a2fc2, 0xc2224f, 0xd99a0b] as const;

/** Высота крышки с ободом над стеклом и отступ сетки внутри стекла, px. */
const GLASS_TOP = 34;
const GRID_INSET = { x: 16, y: 10 } as const;

/** Размер банки по клетке: ширина и высота вместе с крышкой и стеклом. */
export function jarSize(
  columns: number,
  rows: number,
  cell: number,
): { width: number; height: number } {
  return { width: columns * cell + 32, height: 24 + 10 + rows * cell + 24 + 8 };
}

interface Living {
  readonly key: string;
  readonly view: Container;
  readonly stop: () => void;
}

export class JarView extends Container {
  private frameKey = '';
  private readonly back = new Container();
  private readonly front = new Container();
  private readonly grid = new Container();
  private readonly floor = new Graphics();
  private readonly microbeLayer = new Container();
  private readonly pieces = new Graphics();
  private readonly markLayer = new Container();
  private readonly bossLayer = new Container();
  private readonly fog = new Graphics();
  private readonly frost = new Graphics();
  private readonly microbes = new Map<number, Living>();
  private readonly marks = new Map<number, Living>();
  private boss: (Living & { readonly sprite: Sprite }) | null = null;
  private readonly badge = new Container();
  private readonly badgeText = new Text({
    text: '',
    style: textStyle({ size: 14, display: true, weight: '900' }),
  });
  private bossSize = 0;

  constructor(private readonly cell: number) {
    super();
    this.grid.position.set(GRID_INSET.x, GLASS_TOP + GRID_INSET.y);
    this.grid.addChild(
      this.floor,
      this.microbeLayer,
      this.pieces,
      this.markLayer,
      this.bossLayer,
      this.fog,
    );
    this.badgeText.anchor.set(0.5);
    this.addChild(this.back, this.grid, this.front, this.frost);
  }

  /** Размер клетки, px. */
  get cellSize(): number {
    return this.cell;
  }

  /** Центр клетки в координатах банки — для всплывающих чисел и эффектов. */
  cellCenter(x: number, y: number): { x: number; y: number } {
    const s = this.cell;
    return { x: GRID_INSET.x + x * s + s / 2, y: GLASS_TOP + GRID_INSET.y + y * s + s / 2 };
  }

  /** Центр босса в координатах банки; null — босса в банке нет. */
  bossCenter(): { x: number; y: number } | null {
    const boss = this.boss;
    if (!boss) return null;
    return {
      x: GRID_INSET.x + boss.view.x,
      y: GLASS_TOP + GRID_INSET.y + boss.view.y - this.bossSize / 2,
    };
  }

  /** Вспышка урона по боссу. */
  flashBoss(): void {
    const sprite = this.boss?.sprite;
    if (!sprite || motionReduced()) return;
    gsap.killTweensOf(sprite, 'alpha');
    gsap.fromTo(sprite, { alpha: 0.35 }, { alpha: 1, duration: 0.45, ease: 'power2.out' });
  }

  render(model: JarModel): void {
    this.drawFrame(model);
    const s = this.cell;
    const alive = new Set<number>();
    const marked = new Set<number>();

    this.floor.clear();
    this.pieces.clear();
    model.cells.forEach((cell, i) => {
      const x = (i % model.width) * s;
      const y = Math.floor(i / model.width) * s;
      switch (cell.kind) {
        case 'empty':
          this.floor.circle(x + s / 2, y + s / 2, 1.5).fill({ color: 0x8cfff0, alpha: 0.16 });
          break;
        case 'wall':
          wall(this.floor, x, y, s);
          break;
        case 'part':
          tentacle(this.pieces, x, y, s);
          break;
        case 'microbe': {
          alive.add(i);
          this.microbe(i, cell, x, y, model.skin?.hats?.[cell.color]);
          if (cell.mark) {
            marked.add(i);
            this.mark(i, 'ring', x, y);
          }
          break;
        }
        case 'piece':
          drawCapsule(
            this.pieces,
            cell.color,
            cell.side,
            x,
            y,
            s,
            cell.active ?? false,
            model.frost ?? false,
            model.symbols ?? true,
            model.skin?.capsule === 'candy',
          );
          break;
        case 'ghost':
          ghost(this.pieces, cell.side, x, y, s);
          break;
        case 'spawn':
          marked.add(i);
          this.mark(i, 'spawn', x, y);
          break;
      }
    });
    for (const column of model.spawnColumns ?? []) {
      this.floor
        .rect(column * s, 0, s, s)
        .fill({ color: 0xff4f7b, alpha: 0.18 })
        .stroke({ width: 1, color: 0xff4f7b, alpha: 0.6 });
    }
    prune(this.microbes, alive);
    prune(this.marks, marked);
    this.renderBoss(model.boss ?? null);

    this.fog.clear();
    if (model.fog) {
      const fx = model.fog.x * s;
      const fw = model.fog.width * s;
      const fh = model.height * s;
      this.fog.rect(fx, 0, fw, fh).fill({ color: 0x0a2a1c, alpha: 0.96 });
      for (let k = 0; k < model.height; k += 2) {
        this.fog
          .circle(fx + ((k * 13) % fw), k * s + s / 2, s * 0.42)
          .fill({ color: 0x7dffb0, alpha: 0.12 });
      }
    }
    const glassHeight = model.height * s + 24;
    const total = model.width * s + 32;
    this.frost.clear();
    if (model.frost) {
      this.frost
        .roundRect(0, GLASS_TOP, total, glassHeight, 30)
        .fill({ color: 0xbeebff, alpha: 0.1 })
        .stroke({ width: 3, color: 0xc8f0ff, alpha: 0.55 });
    }
  }

  /** Крышка, обод, стекло и блик — перестраиваются только при смене размера. */
  private drawFrame(model: JarModel): void {
    const skin = model.skin?.vessel ?? 'default';
    const look = VESSEL_LOOKS[skin];
    const key = `${model.width}x${model.height}:${skin}`;
    if (key === this.frameKey) return;
    this.frameKey = key;
    for (const layer of [this.back, this.front]) {
      layer.removeChildren().forEach((child) => child.destroy());
    }
    const s = this.cell;
    const inner = model.width * s;
    const total = inner + 32;

    const lid = new Graphics()
      .roundRect((total - (inner - 4)) / 2, 0, inner - 4, 24, 10)
      .fill(look.lid)
      .stroke({ width: 2, color: COLOR.ink });
    for (let x = (total - (inner - 4)) / 2 + 6; x < (total + inner - 4) / 2 - 3; x += 9) {
      lid.rect(x, 3, 3, 18).fill({ color: look.lidStripe, alpha: 0.9 });
    }
    lid
      .roundRect((total - (inner - 4)) / 2 + 4, 2, inner - 12, 3, 2)
      .fill({ color: COLOR.white, alpha: 0.4 });
    const rim = new Graphics()
      .roundRect((total - (inner + 20)) / 2, 24, inner + 20, 10, 3)
      .fill(vertical(look.rim[0], look.rim[1]))
      .stroke({ width: 2, color: COLOR.ink });

    // Стекло: градиент, внутренняя кромка, мягкое свечение.
    const glassHeight = model.height * s + 24;
    const glass = new Graphics();
    glass
      .roundRect(0, GLASS_TOP + 8, total, glassHeight, 36)
      .fill({ color: 0x000000, alpha: 0.35 });
    glass
      .roundRect(0, GLASS_TOP, total, glassHeight, 30)
      .fill(
        new FillGradient({
          type: 'linear',
          start: { x: 0, y: 0 },
          end: { x: 0, y: 1 },
          colorStops: [
            { offset: 0, color: look.glass[0] },
            { offset: 0.58, color: look.glass[1] },
            { offset: 1, color: look.glass[2] },
          ],
          textureSpace: 'local',
        }),
      )
      .stroke({ width: 4, color: 0xbef0ff, alpha: 0.3 });
    glass
      .roundRect(-2, GLASS_TOP - 2, total + 4, glassHeight + 4, 32)
      .stroke({ width: 2, color: COLOR.ink });
    glass
      .roundRect(4, GLASS_TOP + 4, total - 8, glassHeight - 8, 26)
      .stroke({ width: 2, color: 0x04121a, alpha: 0.9 });
    const shine = new Graphics()
      .roundRect(14, GLASS_TOP + 12, 18, glassHeight - 24, 10)
      .fill({ color: COLOR.white, alpha: 0.08 });
    // Декор скина — у дна и по краям, не поверх клеток.
    if (skin === 'aquarium') {
      const weed = new Graphics();
      for (const wx of [10, total - 10]) {
        weed
          .moveTo(wx, GLASS_TOP + glassHeight - 8)
          .bezierCurveTo(
            wx - 6,
            GLASS_TOP + glassHeight - 40,
            wx + 6,
            GLASS_TOP + glassHeight - 70,
            wx,
            GLASS_TOP + glassHeight - 100,
          )
          .stroke({ width: 3, color: COLOR.teal, alpha: 0.7, cap: 'round' });
      }
      glass.addChild(weed);
    } else if (skin === 'snowglobe') {
      const snow = new Graphics();
      for (let k = 0; k < 26; k++) {
        const sx = 8 + ((k * 53) % (total - 16));
        const sy = GLASS_TOP + 10 + ((k * 97) % (glassHeight - 20));
        snow.circle(sx, sy, 1.2 + (k % 3) * 0.6).fill({ color: COLOR.white, alpha: 0.35 });
      }
      snow
        .roundRect(6, GLASS_TOP + glassHeight - 16, total - 12, 10, 5)
        .fill({ color: COLOR.white, alpha: 0.18 });
      glass.addChild(snow);
    }
    this.back.addChild(glass);
    this.front.addChild(shine, rim, lid);
  }

  private microbe(
    index: number,
    cell: Extract<JarCell, { kind: 'microbe' }>,
    x: number,
    y: number,
    hat: 'santa' | 'shades' | undefined,
  ): void {
    const key = `${cell.color}:${cell.armor ?? ''}:${hat ?? ''}`;
    const old = this.microbes.get(index);
    if (old?.key === key) return;
    if (old) drop(old);
    const s = this.cell;
    const box = new Container();
    box.position.set(x + s / 2, y + s / 2);
    // Микроб со скином покачивается вместе с колпаком или очками.
    const body = new Container();
    const size = cell.armor ? s * 0.74 : s;
    body.addChild(artSprite(`microbe-${cell.color}`, size));
    if (hat) {
      body.addChild(
        artSprite(hat === 'santa' ? 'hat-santa' : `hat-shades-${cell.color}`, size * HAT_FRAME),
      );
    }
    box.addChild(body);
    let stop: () => void = () => undefined;
    if (cell.armor) box.addChild(artSprite(cell.armor === 'gold' ? 'armor-gold' : 'armor', s));
    else stop = microbeIdle(body, cell.color, ((index * 7) % 24) / 10);
    this.microbeLayer.addChild(box);
    this.microbes.set(index, { key, view: box, stop });
  }

  /** Отметка будущего эффекта босса: «+» на пустой клетке или кольцо на микробе. */
  private mark(index: number, kind: 'spawn' | 'ring', x: number, y: number): void {
    const old = this.marks.get(index);
    if (old?.key === kind) return;
    if (old) drop(old);
    const s = this.cell;
    let view: Container;
    if (kind === 'spawn') {
      view = spawnMark(x, y, s);
    } else {
      view = new Graphics()
        .circle(0, 0, s / 2 - 1)
        .stroke({ width: 2, color: 0xd7c2ff, alpha: 0.9 });
      view.position.set(x + s / 2, y + s / 2);
    }
    this.markLayer.addChild(view);
    this.marks.set(index, { key: kind, view, stop: idle(view, 'float') });
  }

  private renderBoss(boss: JarBoss | null): void {
    const s = this.cell;
    const key = boss
      ? `${boss.id}:${boss.scream ? 1 : 0}:${boss.x},${boss.y},${boss.width},${boss.height}`
      : '';
    if (this.boss?.key !== key) {
      if (this.boss) {
        this.badge.removeFromParent();
        drop(this.boss);
      }
      this.boss = null;
      if (boss) {
        // Рисунок босса квадратный: вписываем по меньшей стороне, ставим на дно прямоугольника.
        const size = Math.min(boss.width, boss.height) * s + 4;
        this.bossSize = size;
        const box = new Container();
        box.position.set(boss.x * s + (boss.width * s) / 2, (boss.y + boss.height) * s + 2);
        const sprite = artSprite(`boss-${boss.id}${boss.scream ? '-scream' : ''}`, size);
        sprite.anchor.set(0.5, 1);
        box.addChild(sprite);
        this.bossLayer.addChild(box);
        this.boss = { key, view: box, sprite, stop: idle(sprite, 'boss') };
      }
    }
    if (!boss || !this.boss) return;
    const timer = boss.timer ?? '';
    if (!timer) {
      this.badge.removeFromParent();
      return;
    }
    if (this.badge.children.length === 0) {
      const r = (s * 0.74) / 2;
      this.badge.addChild(
        new Graphics()
          .circle(0, 2, r)
          .fill(COLOR.ink)
          .circle(0, 0, r)
          .fill(0xff4f7b)
          .stroke({ width: 2, color: COLOR.ink }),
        this.badgeText,
      );
      this.badgeText.style.fontSize = Math.round(s * 0.36);
    }
    const r = (s * 0.74) / 2;
    this.badge.position.set(-(boss.width * s) / 2 - 12 + r, -boss.height * s - 12 + r);
    this.badgeText.text = timer;
    this.boss.view.addChild(this.badge);
  }

  override destroy(): void {
    for (const living of [...this.microbes.values(), ...this.marks.values()]) living.stop();
    this.boss?.stop();
    if (this.boss) gsap.killTweensOf(this.boss.sprite);
    this.microbes.clear();
    this.marks.clear();
    this.boss = null;
    super.destroy({ children: true });
  }
}

function drop(living: Living): void {
  living.stop();
  living.view.destroy({ children: true });
}

function prune(map: Map<number, Living>, keep: ReadonlySet<number>): void {
  for (const [index, living] of map) {
    if (keep.has(index)) continue;
    drop(living);
    map.delete(index);
  }
}

/** Градиенты половинок капсул: по цвету и ориентации (общие для всех банок). */
const GRADIENTS = new Map<string, FillGradient>();

function capsuleGradient(color: number, vertical: boolean): FillGradient {
  const key = `${color}:${vertical ? 'v' : 'h'}`;
  let gradient = GRADIENTS.get(key);
  if (!gradient) {
    const pal = PAL[color % 3] ?? PAL[0];
    gradient = new FillGradient({
      type: 'linear',
      start: { x: 0, y: 0 },
      end: vertical ? { x: 1, y: 0 } : { x: 0, y: 1 },
      colorStops: [
        { offset: 0, color: LIGHT[color % 3] ?? pal },
        { offset: 0.3, color: pal },
        { offset: 0.64, color: pal },
        { offset: 1, color: DARK[color % 3] ?? pal },
      ],
      textureSpace: 'local',
    });
    GRADIENTS.set(key, gradient);
  }
  return gradient;
}

/** Половина капсулы: градиент по цвету, контур ink, блик и значок цвета. */
export function drawCapsule(
  g: Graphics,
  color: number,
  side: CapsuleSide,
  x: number,
  y: number,
  s: number,
  active: boolean,
  frost: boolean,
  symbols: boolean,
  /** Скин «Леденцы»: белые косые полоски поверх цвета половины. */
  candy = false,
): void {
  const p = Math.max(2, Math.round(s * 0.07));
  const r = Math.round(s * 0.48);
  const sm = 2;
  const bx = x + (side === 'R' ? 0 : p);
  const by = y + (side === 'D' ? 0 : p);
  const w = s - p - (side === 'L' || side === 'R' ? 0 : p);
  const h = s - p - (side === 'U' || side === 'D' ? 0 : p);
  const radii: Record<CapsuleSide, [number, number, number, number]> = {
    L: [r, sm, sm, r],
    R: [sm, r, r, sm],
    U: [r, r, sm, sm],
    D: [sm, sm, r, r],
    S: [r, r, r, r],
  };
  const [tl, tr, br, bl] = radii[side];
  const shape = () =>
    g
      .moveTo(bx + tl, by)
      .lineTo(bx + w - tr, by)
      .arcTo(bx + w, by, bx + w, by + tr, tr)
      .lineTo(bx + w, by + h - br)
      .arcTo(bx + w, by + h, bx + w - br, by + h, br)
      .lineTo(bx + bl, by + h)
      .arcTo(bx, by + h, bx, by + h - bl, bl)
      .lineTo(bx, by + tl)
      .arcTo(bx, by, bx + tl, by, tl)
      .closePath();
  const isVertical = side === 'U' || side === 'D';
  if (active) {
    shape().stroke({ width: 6, color: frost ? 0xbfeaff : COLOR.white, alpha: 0.5 });
  }
  shape().fill(capsuleGradient(color, isVertical)).stroke({ width: 1.5, color: COLOR.ink });
  if (candy) candyStripes(g, bx, by, w, h, Math.max(3, r * 0.35), s);
  // Блик.
  if (isVertical) {
    g.roundRect(bx + w * 0.18, by + h * (side === 'U' ? 0.24 : 0.06), w * 0.16, h * 0.7, 4).fill({
      color: COLOR.white,
      alpha: 0.7,
    });
  } else {
    g.roundRect(bx + w * (side === 'L' ? 0.24 : 0.06), by + h * 0.16, w * 0.7, h * 0.16, 4).fill({
      color: COLOR.white,
      alpha: 0.7,
    });
  }
  if (symbols) {
    const cx = bx + w / 2;
    const cy = by + h / 2;
    const ink =
      color === 2 ? { color: COLOR.ink, alpha: 0.65 } : { color: COLOR.white, alpha: 0.88 };
    if (color === 0)
      g.roundRect(
        cx - s * 0.18,
        cy - Math.max(3, s * 0.1) / 2,
        s * 0.36,
        Math.max(3, s * 0.1),
        2,
      ).fill(ink);
    else if (color === 1) g.circle(cx, cy, s * 0.1).fill(ink);
    else g.circle(cx, cy, s * 0.11).stroke({ width: Math.max(2, s * 0.07), ...ink });
  }
}

/** Косые полоски «Леденцов» внутри половины капсулы (отступ от скруглённых краёв). */
function candyStripes(
  g: Graphics,
  bx: number,
  by: number,
  w: number,
  h: number,
  inset: number,
  s: number,
): void {
  const x0 = bx + inset;
  const y0 = by + inset;
  const x1 = bx + w - inset;
  const y1 = by + h - inset;
  const step = s * 0.3;
  // Прямые x + y = c, обрезанные по внутреннему прямоугольнику.
  for (let c = x0 + y0 + step / 2; c < x1 + y1; c += step) {
    const ax = Math.max(x0, c - y1);
    const bxEnd = Math.min(x1, c - y0);
    if (bxEnd - ax < 1) continue;
    g.moveTo(ax, c - ax).lineTo(bxEnd, c - bxEnd);
  }
  g.stroke({ width: Math.max(2, s * 0.09), color: COLOR.white, alpha: 0.6, cap: 'round' });
}

/** Место приземления активной капсулы: полупрозрачный контур. */
function ghost(g: Graphics, side: CapsuleSide, x: number, y: number, s: number): void {
  const p = Math.max(2, Math.round(s * 0.07));
  const r = Math.round(s * 0.4);
  g.roundRect(
    x + (side === 'R' ? 0 : p),
    y + (side === 'D' ? 0 : p),
    s - p - (side === 'L' || side === 'R' ? 0 : p),
    s - p - (side === 'U' || side === 'D' ? 0 : p),
    r,
  )
    .fill({ color: 0xb4fff5, alpha: 0.06 })
    .stroke({ width: 2, color: 0xb4fff5, alpha: 0.55 });
}

/** Стенка сосуда: толстое стекло без игровых клеток. */
function wall(g: Graphics, x: number, y: number, s: number): void {
  g.rect(x, y, s, s).fill({ color: 0x06141c, alpha: 0.92 });
  g.roundRect(x + 2, y + 2, s - 4, s - 4, 4).fill({ color: 0x9edcf0, alpha: 0.14 });
}

/** Щупальце гибрида: зелёный жгут с присосками. */
function tentacle(g: Graphics, x: number, y: number, s: number): void {
  const w = s * 0.5;
  g.roundRect(x + (s - w) / 2, y + 1, w, s - 2, w / 2)
    .fill(0x2fbf71)
    .stroke({ width: 2, color: COLOR.ink });
  for (let k = 0; k < 3; k++) {
    g.circle(x + s / 2, y + s * (0.24 + k * 0.26), s * 0.07).fill({ color: 0xd9ffe9, alpha: 0.8 });
  }
}

/** Отметка «здесь появится микроб». */
function spawnMark(x: number, y: number, s: number): Container {
  const box = new Container();
  box.position.set(x + s / 2, y + s / 2);
  box.addChild(
    new Graphics()
      .circle(0, 0, s * 0.38)
      .fill({ color: 0xff4f7b, alpha: 0.16 })
      .stroke({ width: 2, color: 0xff8fb0 }),
  );
  const plus = new Text({
    text: '+',
    style: textStyle({ size: Math.round(s * 0.4), display: true, weight: '900', color: 0xffb3c8 }),
  });
  plus.anchor.set(0.5);
  box.addChild(plus);
  return box;
}

/** Картинка банки по строкам токенов макета: «..» пусто, V0 микроб, K0 в биоплёнке, L0/R1 капсула, a — активная. */
export function jarFromRows(rows: readonly string[]): JarCell[] {
  return rows.flatMap((row) =>
    row
      .trim()
      .split(/\s+/)
      .map((token): JarCell => {
        const kind = token[0];
        const color = Number(token[1]);
        const mod = token.slice(2);
        if (kind === 'V' || kind === 'K') {
          return {
            kind: 'microbe',
            color,
            ...(kind === 'K' ? { armor: 'normal' as const } : {}),
            ...(mod === 'M' ? { mark: true } : {}),
          };
        }
        if (kind === 'T') return { kind: 'spawn' };
        if (kind && 'LRUDS'.includes(kind) && !Number.isNaN(color)) {
          const side = kind as CapsuleSide;
          return mod === 'g'
            ? { kind: 'ghost', side }
            : { kind: 'piece', color, side, active: mod === 'a' };
        }
        return { kind: 'empty' };
      }),
  );
}
